import { ApiResponse } from 'apisauce';
import store from 'State/Redux';
import { UserReadonly } from 'Models/Identity/User';
import MfaGlobalConfig from 'Models/Identity/MfaGlobalConfig';
import MfaUserExtension from 'Models/Identity/MfaUserExtension';
import MfaDetailsResponse from 'Models/Identity/MfaDetailsResponse';
import MfaStatistics from 'Models/Identity/MfaStatistics';
import MfaTrustedDevice from 'Models/Identity/MfaTrustedDevice';
import { withAccessToken, createApi } from '.';

export interface IdentityServiceType {
   postPasswordReset(data: UserReadonly): Promise<ApiResponse<number>>;
   postUnlockUser(userId: string): Promise<ApiResponse<string>>;
   postMfaReset(userId: string): Promise<ApiResponse<string>>;
   getMfaConfig(): Promise<ApiResponse<MfaGlobalConfig>>;
   getMfaDetails(subjectId: string): Promise<ApiResponse<MfaUserExtension>>;
   getMyMfaDetails(): Promise<ApiResponse<MfaDetailsResponse>>;
   deleteMfaSmsEnrollment(subjectId: string): Promise<ApiResponse<string>>;
   deleteMfaAuthenticatorEnrollment(
      subjectId: string
   ): Promise<ApiResponse<string>>;
   patchMfaDefaultMethod(
      subjectId: string,
      newMethod: string
   ): Promise<ApiResponse<string>>;
   getMfaStatistics(): Promise<ApiResponse<MfaStatistics>>;
   updateMfaEnforcementDateAndMessage(
      enforcementDate: string | null,
      signinBannerMessage: string | null
   ): Promise<ApiResponse<MfaGlobalConfig>>;
   getTrustedDeviceById(id: string): Promise<ApiResponse<MfaTrustedDevice>>;
   getTrustedDevices(): Promise<ApiResponse<MfaTrustedDevice[]>>;
   revokeDevice(id: string): Promise<ApiResponse<string>>;
}

const create = (baseURL: string | undefined): IdentityServiceType => {
   const api = createApi({ baseURL });

   const postPasswordReset = (
      user: UserReadonly
   ): Promise<ApiResponse<number>> => {
      const authApi = withAccessToken(api, store.getState().oidc);
      return authApi.post('/admin/resetpassword', user);
   };

   const postUnlockUser = (userId: string): Promise<ApiResponse<string>> => {
      const authApi = withAccessToken(api, store.getState().oidc);
      return authApi.post(`/admin/user/unlock/${userId}`);
   };

   const postMfaReset = (userId: string): Promise<ApiResponse<string>> => {
      const authApi = withAccessToken(api, store.getState().oidc);
      return authApi.post(`/admin/mfa/reset/${userId}`);
   };

   const getMfaConfig = (): Promise<ApiResponse<MfaGlobalConfig>> => {
      const authApi = withAccessToken(api, store.getState().oidc);
      return authApi.get('/admin/mfa/config');
   };

   const getMfaDetails = (
      subjectId: string
   ): Promise<ApiResponse<MfaUserExtension>> => {
      const authApi = withAccessToken(api, store.getState().oidc);
      return authApi.get(`/admin/mfa/details/${subjectId}`);
   };

   const getMyMfaDetails = (): Promise<ApiResponse<MfaDetailsResponse>> => {
      const authApi = withAccessToken(api, store.getState().oidc);
      return authApi.get('contact/mfa/details');
   };

   const deleteMfaSmsEnrollment = (
      subjectId: string
   ): Promise<ApiResponse<string>> => {
      const authApi = withAccessToken(api, store.getState().oidc);
      return authApi.delete(`/admin/mfa/sms/${subjectId}`);
   };

   const deleteMfaAuthenticatorEnrollment = (
      subjectId: string
   ): Promise<ApiResponse<string>> => {
      const authApi = withAccessToken(api, store.getState().oidc);
      return authApi.delete(`/admin/mfa/authenticator/${subjectId}`);
   };

   const patchMfaDefaultMethod = (
      subjectId: string,
      newMethod: string
   ): Promise<ApiResponse<string>> => {
      const authApi = withAccessToken(api, store.getState().oidc);
      return authApi.patch(
         `/admin/mfa/default-method/${subjectId}?newMethod=${encodeURIComponent(
            newMethod
         )}`
      );
   };

   const getMfaStatistics = (): Promise<ApiResponse<MfaStatistics>> => {
      const authApi = withAccessToken(api, store.getState().oidc);
      return authApi.get('/admin/mfa/statistics');
   };

   const updateMfaEnforcementDateAndMessage = (
      enforcementDate: string | null,
      signinBannerMessage: string | null
   ): Promise<ApiResponse<MfaGlobalConfig>> => {
      const authApi = withAccessToken(api, store.getState().oidc);
      return authApi.put('/admin/mfa/config/enforcement-date-and-message', {
         enforcementDate,
         enforcementDateMessage: signinBannerMessage,
      });
   };

   const getTrustedDeviceById = (
      id: string
   ): Promise<ApiResponse<MfaTrustedDevice>> => {
      const authApi = withAccessToken(api, store.getState().oidc);
      return authApi.get(`/admin/mfa/trusted-device/${id}`);
   };

   const getTrustedDevices = (): Promise<ApiResponse<MfaTrustedDevice[]>> => {
      const authApi = withAccessToken(api, store.getState().oidc);
      return authApi.get('/admin/mfa/trusted-devices');
   };

   const revokeDevice = (id: string): Promise<ApiResponse<string>> => {
      const authApi = withAccessToken(api, store.getState().oidc);
      return authApi.post(`/admin/mfa/trusted-device/${id}/revoke`);
   };

   return {
      postPasswordReset,
      postUnlockUser,
      postMfaReset,
      getMfaConfig,
      getMfaDetails,
      getMyMfaDetails,
      deleteMfaSmsEnrollment,
      deleteMfaAuthenticatorEnrollment,
      patchMfaDefaultMethod,
      getMfaStatistics,
      updateMfaEnforcementDateAndMessage,
      getTrustedDeviceById,
      getTrustedDevices,
      revokeDevice,
   };
};

export default {
   create,
};
